import React, { useState } from 'react'
import { Card, Table, Tag, Space, Button, Input, Select, Row, Col, Typography, Modal, Form, message } from 'antd'
import {
  PlusOutlined,
  SearchOutlined,
  BookOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExportOutlined,
} from '@ant-design/icons'

const { Title, Paragraph } = Typography
const { Search } = Input
const { Option } = Select
const { TextArea } = Input

// 模拟知识库数据
const knowledgeData = [
  {
    key: '1',
    title: 'Haul 上架最佳实践',
    type: 'best_practice',
    category: '上架',
    tags: ['haul', '上架', '最佳实践'],
    status: 'published',
    author: '张三',
    created_at: '2026-08-01',
    updated_at: '2026-08-30',
    views: 156,
  },
  {
    key: '2',
    title: 'Walmart 质量分提升指南',
    type: 'experience',
    category: '运营',
    tags: ['walmart', '质量分', '运营'],
    status: 'published',
    author: '李四',
    created_at: '2026-08-05',
    updated_at: '2026-08-29',
    views: 234,
  },
  {
    key: '3',
    title: 'Joom 错误码速查表',
    type: 'error_code',
    category: '错误码',
    tags: ['joom', '错误码', '速查'],
    status: 'published',
    author: '王五',
    created_at: '2026-08-08',
    updated_at: '2026-08-28',
    views: 89,
  },
  {
    key: '4',
    title: 'Ozon 物流 SOP',
    type: 'sop',
    category: '物流',
    tags: ['ozon', '物流', 'sop'],
    status: 'reviewing',
    author: '赵六',
    created_at: '2026-08-10',
    updated_at: '2026-08-27',
    views: 45,
  },
  {
    key: '5',
    title: 'Shopify SEO 优化模板',
    type: 'template',
    category: 'SEO',
    tags: ['shopify', 'seo', '模板'],
    status: 'draft',
    author: '钱七',
    created_at: '2026-08-12',
    updated_at: '2026-08-27',
    views: 12,
  },
  {
    key: '6',
    title: '数据分析报表模板',
    type: 'template',
    category: '数据',
    tags: ['数据', '报表', '模板'],
    status: 'published',
    author: '张三',
    created_at: '2026-08-15',
    updated_at: '2026-08-30',
    views: 67,
  },
]

const typeColors: Record<string, string> = {
  experience: '#1890ff',
  best_practice: '#52c41a',
  error_code: '#f50',
  sop: '#722ed1',
  template: '#13c2c2',
}

const statusColors: Record<string, string> = {
  draft: 'default',
  reviewing: 'warning',
  published: 'success',
  archived: 'default',
}

const typeLabels: Record<string, string> = {
  experience: '经验',
  best_practice: '最佳实践',
  error_code: '错误码',
  sop: 'SOP',
  template: '模板',
}

const statusLabels: Record<string, string> = {
  draft: '草稿',
  reviewing: '审核中',
  published: '已发布',
  archived: '已归档',
}

const Knowledge: React.FC = () => {
  const [searchText, setSearchText] = useState('')
  const [filteredData, setFilteredData] = useState(knowledgeData)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [form] = Form.useForm()

  const [filterType, setFilterType] = useState<string | undefined>(undefined)
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined)

  const applyFilters = () => {
    let filtered = knowledgeData

    if (searchText) {
      const q = searchText.toLowerCase()
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.tags.some((tag) => tag.toLowerCase().includes(q))
      )
    }

    if (filterType) {
      filtered = filtered.filter((item) => item.type === filterType)
    }

    if (filterStatus) {
      filtered = filtered.filter((item) => item.status === filterStatus)
    }

    setFilteredData(filtered)
  }

  const handleSearch = (value: string) => {
    setSearchText(value)
    setTimeout(applyFilters, 0)
  }

  const handleAdd = () => {
    setIsModalVisible(true)
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      console.log('Form values:', values)
      message.success('文档创建成功')
      setIsModalVisible(false)
      form.resetFields()
    })
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    form.resetFields()
  }

  const columns = [
    {
      title: '文档标题',
      dataIndex: 'title',
      key: 'title',
      width: 300,
      render: (title: string, record: any) => (
        <Space>
          <BookOutlined style={{ color: typeColors[record.type] }} />
          <a>{title}</a>
        </Space>
      ),
      sorter: (a: any, b: any) => a.title.localeCompare(b.title),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => (
        <Tag color={typeColors[type]}>
          {typeLabels[type]}
        </Tag>
      ),
      filters: Object.entries(typeLabels).map(([value, text]) => ({ text, value })),
      onFilter: (value: any, record: any) => record.type === value,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 200,
      render: (tags: string[]) => (
        <>
          {tags.map((tag) => (
            <Tag key={tag} color="blue">
              {tag}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={statusColors[status]}>
          {statusLabels[status]}
        </Tag>
      ),
      filters: Object.entries(statusLabels).map(([value, text]) => ({ text, value })),
      onFilter: (value: any, record: any) => record.status === value,
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
      width: 100,
    },
    {
      title: '浏览量',
      dataIndex: 'views',
      key: 'views',
      width: 100,
      sorter: (a: any, b: any) => a.views - b.views,
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 120,
      sorter: (a: any, b: any) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: any) => (
        <Space size="middle">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => message.info("查看文档详情")}>
            查看
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => message.info("编辑文档")}>
            编辑
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => {
              Modal.confirm({
                title: '确认删除',
                content: `确定要删除文档「${record.title}」吗？`,
                onOk: () => message.success('文档已删除'),
              })
            }}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4}>知识库</Title>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新建文档
          </Button>
          <Button icon={<ExportOutlined />} onClick={() => message.success("知识库已导出")}>
            导出
          </Button>
        </Space>
      </div>

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Search
              placeholder="搜索文档标题或标签"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
            />
          </Col>
          <Col span={16}>
            <Space style={{ float: 'right' }}>
              <Select
                placeholder="选择类型"
                style={{ width: 120 }}
                allowClear
                value={filterType}
                onChange={(v) => { setFilterType(v); setTimeout(applyFilters, 0); }}
              >
                <Option value="experience">经验</Option>
                <Option value="best_practice">最佳实践</Option>
                <Option value="error_code">错误码</Option>
                <Option value="sop">SOP</Option>
                <Option value="template">模板</Option>
              </Select>
              <Select
                placeholder="选择状态"
                style={{ width: 120 }}
                allowClear
                value={filterStatus}
                onChange={(v) => { setFilterStatus(v); setTimeout(applyFilters, 0); }}
              >
                <Option value="draft">草稿</Option>
                <Option value="reviewing">审核中</Option>
                <Option value="published">已发布</Option>
                <Option value="archived">已归档</Option>
              </Select>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          size="middle"
          scroll={{ x: 1400 }}
        />
      </Card>

      <Modal
        title="新建知识库文档"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="文档标题"
            rules={[{ required: true, message: '请输入文档标题' }]}
          >
            <Input placeholder="请输入文档标题" />
          </Form.Item>
          <Form.Item
            name="type"
            label="文档类型"
            rules={[{ required: true, message: '请选择文档类型' }]}
          >
            <Select placeholder="请选择文档类型">
              <Option value="experience">经验</Option>
              <Option value="best_practice">最佳实践</Option>
              <Option value="error_code">错误码</Option>
              <Option value="sop">SOP</Option>
              <Option value="template">模板</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="category"
            label="分类"
            rules={[{ required: true, message: '请输入分类' }]}
          >
            <Input placeholder="请输入分类" />
          </Form.Item>
          <Form.Item
            name="tags"
            label="标签"
          >
            <Select mode="tags" placeholder="输入标签后回车" tokenSeparators={[',']}>
              <Option value="haul">haul</Option>
              <Option value="walmart">walmart</Option>
              <Option value="joom">joom</Option>
              <Option value="ozon">ozon</Option>
              <Option value="shopify">shopify</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="content"
            label="文档内容"
            rules={[{ required: true, message: '请输入文档内容' }]}
          >
            <TextArea rows={6} placeholder="请输入文档内容" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Knowledge
